/**
 * ProviderForm Component (T116 - US5)
 * Form for editing provider profiles
 * 
 * Note: Provider creation is handled during user registration,
 * this form is for editing existing providers only
 */

import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import type {
  AdminProvider,
  UpdateProviderPayload,
  VettingStatus,
} from '../types/admin-provider.type';
import { PartnerCompanySelect } from '@/features/partner-companies/components/PartnerCompanySelect';
import type { PartnerCompanyOption } from '@/features/partner-companies/types/partner-company.types';

type ProviderFormProps = {
  provider: AdminProvider;
  onSubmit: (payload: UpdateProviderPayload) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
};

type FormData = {
  companyName: string;
  businessAddress: string;
  latitude: string;
  longitude: string;
  vettingStatus: VettingStatus;
  newClientAcceptance: boolean;
  calendlyUrl: string;
  serviceOfferings: string;
};

const VETTING_STATUS_OPTIONS: Array<{ value: VettingStatus; label: string }> = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_REVIEW', label: 'In Review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

/**
 * Provider form component for editing operations
 * 
 * @param provider - Existing provider to edit
 * @param onSubmit - Callback when form is submitted
 * @param onCancel - Callback when form is cancelled
 * @param isSubmitting - Whether form is currently submitting
 * 
 * Features:
 * - Validation for required fields
 * - URL format validation for Calendly
 * - Latitude/longitude validation
 * - Loading state during submission
 */
export function ProviderForm({
  provider,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: ProviderFormProps) {
  const [formData, setFormData] = useState<FormData>({
    companyName: provider.companyName || '',
    businessAddress: provider.businessAddress || '',
    latitude: provider.latitude?.toString() || '',
    longitude: provider.longitude?.toString() || '',
    vettingStatus: provider.vettingStatus,
    newClientAcceptance: provider.newClientAcceptance,
    calendlyUrl: provider.calendlyUrl || '',
    serviceOfferings: provider.serviceOfferings || '',
  });

  const [partnerCompany, setPartnerCompany] = useState<PartnerCompanyOption | null>(
    provider.user.partnerCompany || null,
  );

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Track the initial address to detect changes
  const initialAddressRef = useRef(provider.businessAddress || '');

  // Update form when provider prop changes
  useEffect(() => {
    initialAddressRef.current = provider.businessAddress || '';
    setFormData({
      companyName: provider.companyName || '',
      businessAddress: provider.businessAddress || '',
      latitude: provider.latitude?.toString() || '',
      longitude: provider.longitude?.toString() || '',
      vettingStatus: provider.vettingStatus,
      newClientAcceptance: provider.newClientAcceptance,
      calendlyUrl: provider.calendlyUrl || '',
      serviceOfferings: provider.serviceOfferings || '',
    });
  }, [provider]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    // Company name validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters';
    } else if (formData.companyName.trim().length > 200) {
      newErrors.companyName = 'Company name must not exceed 200 characters';
    }

    // Business address validation
    if (formData.businessAddress && formData.businessAddress.length > 500) {
      newErrors.businessAddress = 'Address must not exceed 500 characters';
    }

    // Latitude and longitude are read-only (auto-resolved from address on backend)

    // Calendly URL validation
    if (formData.calendlyUrl) {
      try {
        const url = new URL(formData.calendlyUrl);
        if (!['http:', 'https:'].includes(url.protocol)) {
          newErrors.calendlyUrl = 'URL must start with http:// or https://';
        }
      } catch {
        newErrors.calendlyUrl = 'Invalid URL format';
      }
    }

    // Service offerings validation
    if (formData.serviceOfferings && formData.serviceOfferings.length < 10) {
      newErrors.serviceOfferings = 'Service offerings must be at least 10 characters';
    } else if (formData.serviceOfferings && formData.serviceOfferings.length > 1000) {
      newErrors.serviceOfferings = 'Service offerings must not exceed 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const payload: UpdateProviderPayload = {
        user: {
          partnerCompanyId: partnerCompany?.id || null,
        },
        provider: {
          companyName: formData.companyName.trim(),
          businessAddress: formData.businessAddress.trim() || undefined,
          latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
          longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
          vettingStatus: formData.vettingStatus,
          newClientAcceptance: formData.newClientAcceptance,
          calendlyUrl: formData.calendlyUrl.trim() || undefined,
          serviceOfferings: formData.serviceOfferings.trim() || undefined,
        },
      };

      await onSubmit(payload);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleChange = (
    field: keyof FormData,
    value: string | boolean | VettingStatus,
  ) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // When address changes from original, clear coordinates (backend will re-geocode)
      if (field === 'businessAddress' && value !== initialAddressRef.current) {
        next.latitude = '';
        next.longitude = '';
      }
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Edit Provider
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Company Name */}
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              id="companyName"
              type="text"
              value={formData.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              disabled={isSubmitting}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                errors.companyName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.companyName && (
              <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
            )}
          </div>

          {/* Partner Company (T043 - US2) */}
          <PartnerCompanySelect
            label="Partner Company"
            value={partnerCompany}
            onChange={setPartnerCompany}
            disabled={isSubmitting}
            placeholder="Search partner companies..."
          />
          {partnerCompany && (
            <p className="text-xs text-amber-700">
              If the selected partner company has SSO enabled, saving will clear the provider&apos;s
              current Kinde identity so their next enterprise sign-in can relink the account.
            </p>
          )}

          {/* Business Address */}
          <div>
            <label htmlFor="businessAddress" className="block text-sm font-medium text-gray-700 mb-1">
              Business Address
            </label>
            <input
              id="businessAddress"
              type="text"
              value={formData.businessAddress}
              onChange={(e) => handleChange('businessAddress', e.target.value)}
              disabled={isSubmitting}
              placeholder="123 Main St, Boston, MA 02101"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                errors.businessAddress ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.businessAddress && (
              <p className="mt-1 text-sm text-red-600">{errors.businessAddress}</p>
            )}
          </div>

          {/* Latitude and Longitude (read-only, auto-resolved from address) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <input
                id="latitude"
                type="text"
                value={formData.latitude}
                disabled
                placeholder="Auto-resolved on save"
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500">Auto-resolved from address on save</p>
            </div>

            <div>
              <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <input
                id="longitude"
                type="text"
                value={formData.longitude}
                disabled
                placeholder="Auto-resolved on save"
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500">Auto-resolved from address on save</p>
            </div>
          </div>

          {/* Vetting Status */}
          <div>
            <label htmlFor="vettingStatus" className="block text-sm font-medium text-gray-700 mb-1">
              Vetting Status <span className="text-red-500">*</span>
            </label>
            <select
              id="vettingStatus"
              value={formData.vettingStatus}
              onChange={(e) => handleChange('vettingStatus', e.target.value as VettingStatus)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {VETTING_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* New Client Acceptance */}
          <div className="flex items-center gap-2">
            <input
              id="newClientAcceptance"
              type="checkbox"
              checked={formData.newClientAcceptance}
              onChange={(e) => handleChange('newClientAcceptance', e.target.checked)}
              disabled={isSubmitting}
              className="h-4 w-4 text-[#026857] border-gray-300 rounded focus:ring-[#026857] disabled:cursor-not-allowed"
            />
            <label htmlFor="newClientAcceptance" className="text-sm font-medium text-gray-700">
              Accepting new clients
            </label>
          </div>

          {/* Calendly URL */}
          <div>
            <label htmlFor="calendlyUrl" className="block text-sm font-medium text-gray-700 mb-1">
              Calendly URL
            </label>
            <input
              id="calendlyUrl"
              type="url"
              value={formData.calendlyUrl}
              onChange={(e) => handleChange('calendlyUrl', e.target.value)}
              disabled={isSubmitting}
              placeholder="https://calendly.com/yourcompany"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                errors.calendlyUrl ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.calendlyUrl && (
              <p className="mt-1 text-sm text-red-600">{errors.calendlyUrl}</p>
            )}
          </div>

          {/* Service Offerings */}
          <div>
            <label htmlFor="serviceOfferings" className="block text-sm font-medium text-gray-700 mb-1">
              Service Offerings
            </label>
            <textarea
              id="serviceOfferings"
              value={formData.serviceOfferings}
              onChange={(e) => handleChange('serviceOfferings', e.target.value)}
              disabled={isSubmitting}
              rows={4}
              placeholder="Describe the services offered..."
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                errors.serviceOfferings ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.serviceOfferings && (
              <p className="mt-1 text-sm text-red-600">{errors.serviceOfferings}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {formData.serviceOfferings.length}/1000 characters
            </p>
          </div>

          {/* Form actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#025246] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Updating...</span>
                </>
              ) : (
                <span>Update Provider</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
